import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchFleetSearchDetails } from "./api";
import { queryKeys } from "../../../../lib/query";

/**
 * Fleet search hook backed by React Query.
 *
 * - Only fetches the full fleet data once (no filtering)
 * - All filtering is now handled client-side in FleetMetrics component
 * - Uses queryKeys.fleet.list({}) which is identical to FleetDataProvider's queryKey,
 *   so the cache is shared — no duplicate call.
 *
 * Returns the same shape as the old reducer: { state: { isLoading, isError, data }, refetch }
 */
function useFleetSearchReducer() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.fleet.list({}), // Always fetch unfiltered data
    queryFn: async () => {
      const result = await fetchFleetSearchDetails({}); // No filter params
      return result?.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const state = useMemo(
    () => ({
      isLoading,
      isError,
      data: data ?? [],
    }),
    [data, isLoading, isError]
  );

  const refetch = useCallback(() => {
    // Simple refetch without filters - just invalidate and refresh
    queryClient.invalidateQueries({ queryKey: queryKeys.fleet.list({}) });
  }, [queryClient]);

  return { state, refetch };
}

export default useFleetSearchReducer;
